import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Building2, 
  Target, 
  TrendingUp, 
  Shield, 
  Users, 
  FileCheck, 
  Scale, 
  Clock, 
  ChevronRight,
  Send,
  Briefcase,
  Handshake,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: "Stratégie", href: "#strategie" },
    { label: "Performance", href: "#performance" },
    { label: "Structuration", href: "#structuration" },
    { label: "Processus", href: "#processus" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/50' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="text-xl font-bold text-slate-50 tracking-tight" data-testid="logo">
            Club Deal Immo
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="text-sm text-slate-400 hover:text-slate-50 transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                data-testid="nav-cta-button"
                className="bg-white text-slate-950 hover:bg-slate-200 font-medium px-6 py-2 rounded-full transition-all"
              >
                Nous contacter
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-slate-50 p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/50 py-6">
            <div className="flex flex-col gap-4 px-6">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="text-slate-300 hover:text-slate-50 transition-colors py-2"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setIsOpen(false)}>
                <Button 
                  data-testid="mobile-nav-cta"
                  className="w-full bg-white text-slate-950 hover:bg-slate-200 font-medium py-3 rounded-full mt-2"
                >
                  Nous contacter
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center pt-20 pattern-bg"
    >
      {/* Abstract background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-900/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <div className="animate-fade-in-up">
          <span className="inline-block text-sm text-slate-400 uppercase tracking-widest mb-6">
            Côte d'Azur • Club Deals Immobiliers
          </span>
        </div>
        
        <h1 
          data-testid="hero-title"
          className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-slate-50 mb-6 animate-fade-in-up delay-100"
        >
          <span className="gradient-text">Accédez</span>
          <br />
          <span className="text-slate-50">à l'invisible</span>
        </h1>
        
        <p 
          data-testid="hero-subtitle"
          className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200"
        >
          Participez à des opérations immobilières structurées aux côtés d'un opérateur local 
          spécialisé sur la Côte d'Azur, de Menton à St Tropez. Performance et discrétion.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
          <a href="#contact">
            <Button 
              data-testid="hero-cta-primary"
              className="bg-white text-slate-950 hover:bg-slate-200 font-medium px-8 py-6 rounded-full text-base transition-all hover:-translate-y-1"
            >
              Accéder aux opportunités
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <a href="#strategie">
            <Button 
              data-testid="hero-cta-secondary"
              variant="outline"
              className="bg-transparent border border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white px-8 py-6 rounded-full text-base transition-all"
            >
              Découvrir notre approche
            </Button>
          </a>
        </div>

        {/* Stats preview */}
        <div className="grid grid-cols-3 gap-6 mt-20 max-w-2xl mx-auto animate-fade-in-up delay-400">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-slate-50">14-25%</div>
            <div className="text-xs text-slate-500 mt-1">Rendement visé</div>
          </div>
          <div className="text-center border-x border-slate-800">
            <div className="text-2xl md:text-3xl font-bold text-slate-50">12-36</div>
            <div className="text-xs text-slate-500 mt-1">Mois de durée</div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-slate-50">100k€+</div>
            <div className="text-xs text-slate-500 mt-1">Ticket minimum</div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronRight className="w-6 h-6 text-slate-600 rotate-90" />
      </div>
    </section>
  );
};

// Strategy Section with Bento Grid
const StrategySection = () => {
  const strategies = [
    {
      icon: Building2,
      title: "Marchand de Biens",
      description: "Acquisition, transformation et revente d'actifs immobiliers avec création de valeur significative.",
    },
    {
      icon: Target,
      title: "Repositionnement Stratégique",
      description: "Valorisation d'actifs sous-exploités par changement d'usage ou amélioration de leur positionnement marché.",
    },
    {
      icon: TrendingUp,
      title: "Division & Restructuration",
      description: "Optimisation du foncier par division parcellaire ou restructuration d'ensembles immobiliers.",
    },
    {
      icon: BarChart3,
      title: "Optimisation Foncière",
      description: "Identification et exploitation du potentiel constructible de terrains sous-valorisés.",
    },
  ];

  const principles = [
    "Acquisition sous valeur de marché",
    "Stratégie de sortie définie en amont",
    "Calendrier d'exécution maîtrisé",
    "Véhicule juridique dédié",
  ];

  return (
    <section id="strategie" data-testid="strategy-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-sm text-slate-500 uppercase tracking-widest">Notre Stratégie</span>
          <h2 
            data-testid="strategy-title"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-50 mt-4"
          >
            Une logique entrepreneuriale<br />claire et disciplinée
          </h2>
          <p className="text-base text-slate-400 max-w-2xl mx-auto mt-6">
            Identifier une décote réelle. Structurer l'opération. Créer de la valeur. Arbitrer avec discipline.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {strategies.map((strategy, index) => (
            <div
              key={strategy.title}
              data-testid={`strategy-card-${index}`}
              className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/50 p-8 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-900/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <strategy.icon className="w-10 h-10 text-slate-500 group-hover:text-blue-400 transition-colors mb-6" />
              <h3 className="text-lg font-semibold text-slate-50 mb-3">{strategy.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{strategy.description}</p>
            </div>
          ))}
        </div>

        {/* Principles */}
        <div className="glass-card rounded-2xl p-8 md:p-12">
          <h3 className="text-xl font-semibold text-slate-50 mb-8 text-center">
            Chaque projet repose sur
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {principles.map((principle, index) => (
              <div 
                key={index}
                data-testid={`principle-${index}`}
                className="flex items-center gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{principle}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Performance Section
const PerformanceSection = () => {
  return (
    <section id="performance" data-testid="performance-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950/10 to-slate-950"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-sm text-slate-500 uppercase tracking-widest">Performance</span>
          <h2 
            data-testid="performance-title"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-50 mt-4"
          >
            Objectifs de rendement ambitieux
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div 
            data-testid="stat-rendement"
            className="flex flex-col items-center justify-center p-10 rounded-2xl bg-slate-900/30 border border-slate-800/50 animate-pulse-glow"
          >
            <span className="text-5xl md:text-6xl font-bold text-slate-50">14-25%</span>
            <span className="text-slate-400 mt-3">Rendement annuel visé</span>
          </div>
          <div 
            data-testid="stat-duree"
            className="flex flex-col items-center justify-center p-10 rounded-2xl bg-slate-900/30 border border-slate-800/50"
          >
            <span className="text-5xl md:text-6xl font-bold text-slate-50">12-36</span>
            <span className="text-slate-400 mt-3">Mois de durée moyenne</span>
          </div>
          <div 
            data-testid="stat-ticket"
            className="flex flex-col items-center justify-center p-10 rounded-2xl bg-slate-900/30 border border-slate-800/50"
          >
            <span className="text-5xl md:text-6xl font-bold text-slate-50">100k€</span>
            <span className="text-slate-400 mt-3">Ticket d'entrée minimum</span>
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 max-w-2xl mx-auto">
          Ces objectifs sont établis sur la base d'analyses financières détaillées et d'hypothèses prudentes de valorisation. 
          Les performances passées ne préjugent pas des performances futures.
        </p>
      </div>
    </section>
  );
};

// Alignment Section
const AlignmentSection = () => {
  return (
    <section id="alignement" data-testid="alignment-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-sm text-slate-500 uppercase tracking-widest">Alignement & Engagement</span>
            <h2 
              data-testid="alignment-title"
              className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-4 mb-6"
            >
              Nos intérêts sont<br />parfaitement alignés
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Nous intervenons systématiquement au capital des opérations. Notre rémunération est directement 
              corrélée à la performance globale du projet, via un mécanisme transparent de partage de résultat 
              et, le cas échéant, de success fee.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-50">Co-investissement systématique</h4>
                  <p className="text-sm text-slate-400 mt-1">L'opérateur investit au capital de chaque opération</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-50">Rémunération à la performance</h4>
                  <p className="text-sm text-slate-400 mt-1">Success fee indexé sur les résultats du projet</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            {/* Abstract geometric pattern */}
            <div className="aspect-square rounded-2xl glass-card p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-amber-900/10"></div>
              <div className="absolute top-8 left-8 w-24 h-24 border-2 border-slate-700/50 rounded-xl"></div>
              <div className="absolute top-16 left-16 w-32 h-32 border-2 border-blue-800/30 rounded-xl"></div>
              <div className="absolute bottom-8 right-8 w-40 h-40 border-2 border-slate-700/50 rounded-full"></div>
              <div className="absolute bottom-20 right-20 w-20 h-20 bg-amber-900/20 rounded-full blur-xl"></div>
              
              <div className="relative z-10 h-full flex flex-col justify-center items-center text-center">
                <Shield className="w-16 h-16 text-slate-600 mb-6" />
                <p className="text-slate-400 text-lg italic">
                  "Une logique qui garantit un alignement d'intérêts total entre l'opérateur et ses partenaires."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Structure Section
const StructureSection = () => {
  const benefits = [
    { icon: FileCheck, text: "Documentation juridique complète" },
    { icon: Scale, text: "Pacte d'associés formalisé" },
    { icon: Users, text: "Encadrement par avocat spécialisé" },
    { icon: BarChart3, text: "Visibilité sur la stratégie et les flux" },
  ];

  return (
    <section id="structuration" data-testid="structure-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-sm text-slate-500 uppercase tracking-widest">Structuration & Encadrement</span>
          <h2 
            data-testid="structure-title"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-50 mt-4"
          >
            Un cadre juridique<br />rigoureux et transparent
          </h2>
          <p className="text-base text-slate-400 max-w-2xl mx-auto mt-6">
            Chaque opération est réalisée via une structure dédiée (SAS ou véhicule adapté).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              data-testid={`benefit-${index}`}
              className="group flex flex-col items-center text-center p-8 rounded-2xl border border-slate-800 bg-slate-950/30 hover:border-slate-700 transition-all"
            >
              <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center mb-5 group-hover:bg-slate-800 transition-colors">
                <benefit.icon className="w-7 h-7 text-slate-400 group-hover:text-slate-300" />
              </div>
              <p className="text-slate-300 text-sm">{benefit.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900/50 border border-slate-800">
            <Clock className="w-5 h-5 text-slate-500" />
            <span className="text-slate-400 text-sm">Durée indicative des opérations : <span className="text-slate-200 font-medium">12 à 36 mois</span></span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partner Profile Section
const PartnerSection = () => {
  const profiles = [
    "Recherchant une performance dynamique dans un cadre structuré",
    "Sensibles à la logique entrepreneuriale",
    "Disposant d'une capacité d'investissement de 100 000 € à plusieurs millions d'euros",
    "Souhaitant s'inscrire dans une relation de confiance et de long terme",
  ];

  return (
    <section data-testid="partner-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-amber-950/5 to-slate-950"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-sm text-slate-500 uppercase tracking-widest">Profil des Partenaires</span>
          <h2 
            data-testid="partner-title"
            className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-4 mb-12"
          >
            Nous privilégions des investisseurs
          </h2>

          <div className="space-y-4 text-left">
            {profiles.map((profile, index) => (
              <div
                key={index}
                data-testid={`partner-profile-${index}`}
                className="flex items-start gap-4 p-5 rounded-xl bg-slate-900/30 border border-slate-800/50"
              >
                <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">{profile}</span>
              </div>
            ))}
          </div>

          <p className="mt-10 text-slate-500 text-sm italic">
            Chaque opération fait l'objet d'une présentation individuelle et confidentielle.
          </p>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    { 
      number: "01", 
      title: "Prise de contact", 
      description: "Soumettez votre candidature via notre formulaire confidentiel" 
    },
    { 
      number: "02", 
      title: "Échange confidentiel", 
      description: "Discussion approfondie sur vos objectifs et votre profil investisseur" 
    },
    { 
      number: "03", 
      title: "Présentation détaillée", 
      description: "Accès aux informations complètes d'une opération spécifique" 
    },
    { 
      number: "04", 
      title: "Participation", 
      description: "Investissement dans le cadre juridique dédié à l'opération" 
    },
  ];

  return (
    <section id="processus" data-testid="process-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-sm text-slate-500 uppercase tracking-widest">Processus d'Accès</span>
          <h2 
            data-testid="process-title"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-50 mt-4"
          >
            Un parcours structuré<br />et confidentiel
          </h2>
          <p className="text-base text-slate-400 max-w-2xl mx-auto mt-6">
            Les opérations ne font pas l'objet d'une diffusion publique ni d'un appel généralisé.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={step.number}
              data-testid={`process-step-${index}`}
              className="relative group"
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-1/2 w-full h-px bg-gradient-to-r from-slate-700 to-transparent"></div>
              )}
              
              <div className="relative z-10 flex flex-col items-center text-center p-8 rounded-2xl border border-slate-800 bg-slate-950/50 hover:border-slate-700 transition-all group-hover:-translate-y-1">
                <div className="text-4xl font-bold text-slate-700 mb-4 group-hover:text-blue-900 transition-colors">
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold text-slate-50 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    investment_capacity: "",
    sector: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const investmentCapacities = [
    "100 000 € - 250 000 €",
    "250 000 € - 500 000 €",
    "500 000 € - 1 000 000 €",
    "1 000 000 € - 2 500 000 €",
    "Plus de 2 500 000 €",
  ];

  const sectors = [
    "Immobilier",
    "Finance / Banque",
    "Industrie",
    "Technologies",
    "Commerce",
    "Professions libérales",
    "Retraité",
    "Autre",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("Votre demande a été envoyée avec succès", {
        description: "Nous vous recontacterons dans les plus brefs délais.",
      });
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        investment_capacity: "",
        sector: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue", {
        description: "Veuillez réessayer ultérieurement.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 pattern-bg"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left side - Info */}
          <div className="flex flex-col justify-center">
            <span className="text-sm text-slate-500 uppercase tracking-widest">Contact</span>
            <h2 
              data-testid="contact-title"
              className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-4 mb-6"
            >
              Candidature<br />Exclusive
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Vous souhaitez accéder à nos opérations de co-investissement immobilier ? 
              Complétez ce formulaire pour initier un échange confidentiel.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-slate-400">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-sm">Vos informations restent strictement confidentielles</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="text-sm">Réponse sous 48h ouvrées</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <Briefcase className="w-5 h-5 text-blue-400" />
                <span className="text-sm">Présentation personnalisée des opportunités</span>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="glass-card rounded-2xl p-8 md:p-10">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 mb-6" />
                <h3 className="text-2xl font-semibold text-slate-50 mb-3">Demande envoyée</h3>
                <p className="text-slate-400">Nous vous recontacterons très prochainement.</p>
                <Button
                  data-testid="new-request-btn"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 bg-transparent border border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Nouvelle demande
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="full_name" className="text-slate-300 text-sm">Nom complet *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      data-testid="input-fullname"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 placeholder:text-slate-600 rounded-lg h-12"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300 text-sm">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 placeholder:text-slate-600 rounded-lg h-12"
                      placeholder="jean@example.com"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-300 text-sm">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 placeholder:text-slate-600 rounded-lg h-12"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="investment_capacity" className="text-slate-300 text-sm">Capacité d'investissement *</Label>
                    <Select
                      value={formData.investment_capacity}
                      onValueChange={(value) => setFormData({ ...formData, investment_capacity: value })}
                      required
                    >
                      <SelectTrigger 
                        data-testid="select-investment-capacity"
                        className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 rounded-lg h-12"
                      >
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {investmentCapacities.map((capacity) => (
                          <SelectItem 
                            key={capacity} 
                            value={capacity}
                            className="text-slate-200 focus:bg-slate-800 focus:text-slate-50"
                          >
                            {capacity}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="sector" className="text-slate-300 text-sm">Secteur d'activité *</Label>
                    <Select
                      value={formData.sector}
                      onValueChange={(value) => setFormData({ ...formData, sector: value })}
                      required
                    >
                      <SelectTrigger 
                        data-testid="select-sector"
                        className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 rounded-lg h-12"
                      >
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {sectors.map((sector) => (
                          <SelectItem 
                            key={sector} 
                            value={sector}
                            className="text-slate-200 focus:bg-slate-800 focus:text-slate-50"
                          >
                            {sector}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-300 text-sm">Message (optionnel)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="mt-2 bg-slate-950/50 border-slate-800 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 text-slate-200 placeholder:text-slate-600 rounded-lg resize-none"
                    placeholder="Précisez vos objectifs d'investissement ou toute information utile..."
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-white text-slate-950 hover:bg-slate-200 font-medium py-6 rounded-full text-base transition-all disabled:opacity-50"
                >
                  {isSubmitting ? (
                    "Envoi en cours..."
                  ) : (
                    <>
                      Envoyer ma candidature
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="border-t border-slate-800 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Legal Notice */}
        <div className="mb-10 p-6 rounded-xl bg-slate-900/30 border border-slate-800/50">
          <h4 className="text-sm font-medium text-slate-400 mb-3">Mention réglementaire</h4>
          <p className="text-xs text-slate-500 leading-relaxed">
            Les informations présentées sur cette page ont un caractère exclusivement informatif. 
            Elles ne constituent ni une offre au public de titres financiers, ni une sollicitation, ni un conseil en investissement. 
            Toute participation intervient dans le cadre d'une documentation contractuelle spécifique et comporte une part d'aléa 
            inhérente à toute opération immobilière. Les objectifs de performance sont indicatifs et ne constituent pas un engagement contractuel.
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <span className="text-slate-500 text-sm">
              © {currentYear} Club Deal Immo. Tous droits réservés.
            </span>
            <a 
              href="mailto:contact@clubdealimmo06.com" 
              className="text-sm text-slate-400 hover:text-slate-200 transition-colors"
              data-testid="footer-email"
            >
              contact@clubdealimmo06.com
            </a>
          </div>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-testid="link-mentions">
              Mentions légales
            </a>
            <a href="#" className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-testid="link-privacy">
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#020617]">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <StrategySection />
        <PerformanceSection />
        <AlignmentSection />
        <StructureSection />
        <PartnerSection />
        <ProcessSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
